import { useState } from "react";
import { HiX } from "react-icons/hi";
import { CustomLabel, CustomInput, CustomButton, CustomToggle } from "../../../components/FormFields";
import { INITIAL_CATEGORIES } from "../../../data/employeeCategories";

const ALL_LEAVE_TYPES = [
  { id: 1, name: "Annual Leave",    defaultDays: 14 },
  { id: 2, name: "Sick Leave",      defaultDays: 7  },
  { id: 3, name: "Casual Leave",    defaultDays: 7  },
  { id: 4, name: "No-Pay Leave",    defaultDays: 0  },
  { id: 5, name: "Maternity Leave", defaultDays: 84 },
  { id: 6, name: "Study Leave",     defaultDays: 5  },
];

function initEntitlements(initialData) {
  if (initialData?.entitlements) {
    return ALL_LEAVE_TYPES.map((lt) => {
      const existing = initialData.entitlements.find((e) => e.id === lt.id);
      return existing
        ? { ...lt, enabled: true,  daysPerYear: existing.daysPerYear }
        : { ...lt, enabled: false, daysPerYear: lt.defaultDays };
    });
  }
  return ALL_LEAVE_TYPES.map((lt) => ({ ...lt, enabled: false, daysPerYear: lt.defaultDays }));
}

export default function LeavePolicyForm({ closeForm, initialData, onSave }) {
  const [name, setName]           = useState(initialData?.name ?? "");
  const [description, setDesc]    = useState(initialData?.description ?? "");
  const [appliesTo, setAppliesTo] = useState(initialData?.appliesTo ?? []);
  const [entitlements, setEnt]    = useState(() => initEntitlements(initialData));

  function toggleCategory(catName) {
    setAppliesTo((prev) =>
      prev.includes(catName) ? prev.filter((c) => c !== catName) : [...prev, catName]
    );
  }

  function toggleLeave(id) {
    setEnt((prev) => prev.map((e) => e.id === id ? { ...e, enabled: !e.enabled } : e));
  }

  function setDays(id, val) {
    setEnt((prev) => prev.map((e) => e.id === id ? { ...e, daysPerYear: Number(val) } : e));
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSave({
      id:           initialData?.id ?? Date.now(),
      name,
      description,
      appliesTo,
      entitlements: entitlements
        .filter((e) => e.enabled)
        .map(({ id, name, daysPerYear }) => ({ id, name, daysPerYear })),
    });
    closeForm();
  }

  const categories = INITIAL_CATEGORIES;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-gray-800">

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl bg-purple-600 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {initialData ? "Edit Leave Policy" : "Create Leave Policy"}
            </h3>
            <p className="text-sm text-purple-200">Define entitlements per employee category</p>
          </div>
          <button type="button" onClick={closeForm}
            className="rounded-lg p-1.5 text-purple-200 hover:bg-purple-500 hover:text-white">
            <HiX className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 px-6 py-6">

          {/* Policy Name */}
          <div className="flex flex-col gap-1.5">
            <CustomLabel htmlFor="policyName">Policy Name</CustomLabel>
            <CustomInput
              id="policyName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Permanent Staff Policy"
              required
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <CustomLabel htmlFor="policyDesc">Description</CustomLabel>
            <CustomInput
              id="policyDesc"
              value={description}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="e.g. Standard leave for all permanent employees"
            />
          </div>

          {/* Applies To */}
          <div className="flex flex-col gap-2">
            <CustomLabel>Applies To (Employee Categories)</CustomLabel>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => {
                const selected = appliesTo.includes(cat.name);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.name)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      selected
                        ? "border-purple-500 bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                        : "border-gray-200 text-gray-500 hover:border-purple-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-purple-500"
                    }`}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
            {appliesTo.length === 0 && (
              <p className="text-xs text-amber-500">Select at least one category to apply this policy</p>
            )}
          </div>

          {/* Leave Entitlements */}
          <div className="flex flex-col gap-2">
            <CustomLabel>Leave Entitlements</CustomLabel>
            <p className="text-xs text-gray-400">Toggle each leave type to include it, then set the annual entitlement in days.</p>
            <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              {entitlements.map((ent, idx) => (
                <div
                  key={ent.id}
                  className={`flex items-center justify-between gap-4 px-4 py-3 transition-colors ${
                    idx !== entitlements.length - 1 ? "border-b border-gray-100 dark:border-gray-700" : ""
                  } ${ent.enabled ? "bg-purple-50/60 dark:bg-purple-900/10" : ""}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <CustomToggle checked={ent.enabled} onChange={() => toggleLeave(ent.id)} color="purple" />
                    <span className={`text-sm truncate ${
                      ent.enabled
                        ? "font-medium text-gray-800 dark:text-gray-200"
                        : "text-gray-400 dark:text-gray-500"
                    }`}>
                      {ent.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={ent.daysPerYear}
                      onChange={(e) => setDays(ent.id, e.target.value)}
                      disabled={!ent.enabled}
                      className={`w-16 rounded-md border px-2 py-1.5 text-center text-sm transition-colors
                        ${ent.enabled
                          ? "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          : "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed dark:border-gray-700 dark:bg-gray-800 dark:text-gray-600"
                        } focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
                    />
                    <span className="w-12 text-xs text-gray-400">days/yr</span>
                  </div>
                </div>
              ))}
            </div>
            {entitlements.filter((e) => e.enabled).length === 0 && (
              <p className="text-xs text-amber-500">Enable at least one leave type</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-2 dark:border-gray-700">
            <CustomButton color="gray" type="button" onClick={closeForm}>Cancel</CustomButton>
            <CustomButton
              color="purple"
              type="submit"
              disabled={appliesTo.length === 0 || entitlements.filter((e) => e.enabled).length === 0}
            >
              {initialData ? "Update Policy" : "Create Policy"}
            </CustomButton>
          </div>
        </form>
      </div>
    </div>
  );
}
